import React, { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { getUser } from "../../lib/authToken";
import { DEV_USER } from "../../config/localAuth";

const TYPE_OPTIONS = [
  { value: "MEETING", label: "Meeting" },
  { value: "APPOINTMENT", label: "Appointment" },
  { value: "PRESENTATION", label: "Presentation" },
  { value: "EVENT", label: "Event" },
];

function uid() {
  return "EVT-" + Math.random().toString(36).slice(2, 9) + Date.now().toString(36);
}

function Chip({ children, onRemove }) {
  return (
    <span className="chip">
      {children}
      <button type="button" className="chip-x" onClick={onRemove} aria-label="Remove">×</button>
    </span>
  );
}

export default function AddScheduleModal({
  open,
  onClose,
  onCreate,
  onUpdate,
  colors = {},
  defaultDate,
  mode = "create",
  initialEvent = null
}) {
  const overlayRef = useRef(null);
  const initDate = useMemo(() => {
    if (defaultDate) {
      const date = new Date(defaultDate);
      return date instanceof Date && !isNaN(date) ? date : new Date();
    }
    return new Date();
  }, [defaultDate]);

  const [title, setTitle] = useState("");
  const [type, setType] = useState("MEETING");
  const [allDay, setAllDay] = useState(false);
  const [startDate, setStartDate] = useState(format(initDate, "yyyy-MM-dd"));
  const [startTime, setStartTime] = useState("09:00");
  const [endDate, setEndDate] = useState(format(initDate, "yyyy-MM-dd"));
  const [endTime, setEndTime] = useState("10:00");
  const [assignees, setAssignees] = useState([]);
  const [assigneeInput, setAssigneeInput] = useState("");
  const [links, setLinks] = useState("");
  const [notes, setNotes] = useState("");
  const [importance, setImportance] = useState("normal");

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && initialEvent) {
      const s = new Date(initialEvent.start);
      const e = new Date(initialEvent.end);
      const isValidStart = s instanceof Date && !isNaN(s);
      const isValidEnd = e instanceof Date && !isNaN(e);
      setTitle(initialEvent.title || "");
      setType(initialEvent.type || "MEETING");
      setAllDay(!!initialEvent.all_day);
      setStartDate(isValidStart ? format(s, "yyyy-MM-dd") : format(initDate, "yyyy-MM-dd"));
      setEndDate(isValidEnd ? format(e, "yyyy-MM-dd") : format(initDate, "yyyy-MM-dd"));
      setStartTime(isValidStart ? format(s, "HH:mm") : "09:00");
      setEndTime(isValidEnd ? format(e, "HH:mm") : "10:00");
      setAssignees(Array.isArray(initialEvent.assignees) ? initialEvent.assignees.slice() : []);
      setAssigneeInput("");
      setLinks(initialEvent.links || "");
      setNotes(initialEvent.notes || "");
      setImportance(initialEvent.importance || "normal");
    } else {
      setTitle("");
      setType("MEETING");
      setAllDay(false);
      const dStr = format(initDate, "yyyy-MM-dd");
      setStartDate(dStr);
      setEndDate(dStr);
      setStartTime("09:00");
      setEndTime("10:00");
      setAssignees([]);
      setAssigneeInput("");
      setLinks("");
      setNotes("");
      setImportance("normal");
    }
  }, [open, mode, initialEvent, defaultDate, initDate]);

  if (!open) return null;

  function addAssignee() {
    const name = assigneeInput.trim();
    if (!name) return;
    setAssignees(prev => [...prev, name]);
    setAssigneeInput("");
  }
  function removeAssignee(idx) {
    setAssignees(prev => prev.filter((_, i) => i !== idx));
  }

  function onOverlayClick(e) { if (e.target === overlayRef.current) onClose(); }

  function handleSubmit(e) {
    e.preventDefault();
    if (!title.trim()) return alert("Please enter a title.");

    const startISO = allDay ? new Date(`${startDate}T00:00:00`) : new Date(`${startDate}T${startTime}:00`);
    const endISO   = allDay ? new Date(`${endDate}T23:59:59`) : new Date(`${endDate}T${endTime}:00`);
    if (+endISO <= +startISO) return alert("End time must be after start time.");

    const cleanLinks = links.trim();

    const user = getUser() || DEV_USER;
    const restaurantId = user.restaurant_id;

    // Format start and end in IST (Asia/Kolkata) as "YYYY-MM-DD HH:mm:ss"
    const startIST = formatInTimeZone(startISO, "Asia/Kolkata", "yyyy-MM-dd HH:mm:ss");
    const endIST = formatInTimeZone(endISO, "Asia/Kolkata", "yyyy-MM-dd HH:mm:ss");

    const payload = {
      id: mode === "edit" && initialEvent?.id ? initialEvent.id : uid(),
      type,
      title: title.trim(),
      start: startIST,
      end: endIST,
      all_day: !!allDay,
      assignees: assignees.slice(),
      links: cleanLinks,
      notes: notes.trim(),
      importance,
      restaurant_id: restaurantId,
      version: initialEvent?.version || 1,
      createdAt: initialEvent?.createdAt || new Date().toISOString(),
      is_active: true,
      location_id: initialEvent?.location_id || undefined,
      room: initialEvent?.room || undefined,
    };

    if (mode === "edit" && onUpdate) onUpdate(payload);
    else if (onCreate) onCreate(payload);

    onClose();
  }

  return (
    <div className="modal-overlay" ref={overlayRef} onClick={onOverlayClick}>
      <div className="modal">
        <div className="modal-head">
          <h3 className="card-title">{mode === "edit" ? "Edit Schedule" : "Add Schedule"}</h3>
          <button className="btn-icon" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <form className="form form-grid" onSubmit={handleSubmit}>
          <div className="form-row">
            <label htmlFor="sch-title">Header</label>
            <input
              id="sch-title"
              type="text"
              className="input"
              placeholder="e.g., Weekly Specials Review"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="form-row">
            <label htmlFor="sch-type">Category</label>
            <div className="row-inline">
              <select
                id="sch-type"
                className="input"
                value={type}
                onChange={(e) => setType(e.target.value)}
                style={{ borderColor: colors[type] || "#eee3da" }}
              >
                {TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <div className="form-checkbox">
                <label className="label">All Day</label>
                <label className="switch">
                  <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
                  <span className="slider" />
                </label>
              </div>
            </div>
          </div>
          <div className="form-row">
            <label>Start</label>
            <div className="row-inline">
              <input
                type="date"
                className="input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              {!allDay && (
                <input
                  type="time"
                  className="input"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              )}
            </div>
          </div>
          <div className="form-row">
            <label>End</label>
            <div className="row-inline">
              <input
                type="date"
                className="input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
              {!allDay && (
                <input
                  type="time"
                  className="input"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              )}
            </div>
          </div>
          <div className="form-row">
            <label>Priority</label>
            <div className="segmented segmented-sm" role="radiogroup" aria-label="Priority">
              <button
                type="button"
                role="radio"
                aria-checked={importance === "normal"}
                className={`priority-btn ${importance === "normal" ? "is-active priority-normal" : "priority-normal"}`}
                onClick={() => setImportance("normal")}
              >
                Normal
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={importance === "important"}
                className={`priority-btn ${importance === "important" ? "is-active priority-important" : "priority-important"}`}
                onClick={() => setImportance("important")}
                title="Marks the event with a star and highlight"
              >
                Important ★
              </button>
            </div>
          </div>
          <div className="form-row">
            <label>Team</label>
            <div className="chips-input">
              {assignees.map((n, idx) => (
                <Chip key={idx} onRemove={() => removeAssignee(idx)}>{n}</Chip>
              ))}
              <div className="row-inline">
                <input
                  type="text"
                  className="input input-inline"
                  placeholder="Type a name and press Enter"
                  value={assigneeInput}
                  onChange={(e) => setAssigneeInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addAssignee();
                    }
                  }}
                />
                <button type="button" className="btn" onClick={addAssignee}>Add</button>
              </div>
            </div>
          </div>
          <div className="form-row">
            <label htmlFor="sch-links">Links</label>
            <input
              id="sch-links"
              type="url"
              className="input"
              placeholder="https://…"
              value={links}
              onChange={(e) => setLinks(e.target.value)}
            />
          </div>
          <div className="form-row">
            <label htmlFor="sch-notes">Notes</label>
            <textarea
              id="sch-notes"
              className="input"
              rows="4"
              placeholder="Anything important to remember…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <div className="form-actions">
            <button type="button" className="btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn is-primary">
              {mode === "edit" ? "Save Changes" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
